import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, AlertIcon, Box, Button, Center, Icon, Spinner, Text, VStack, useToast } from '@chakra-ui/react'
import { FiShare2 } from 'react-icons/fi'
import { FileItem, FileLinkRelationType, RelatedFileEntry } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useElementSize } from '@/hooks/useElementSize'
import { useLinkGraph } from './useLinkGraph'
import { explicitLinkBetween, relatedLinkBetween, type GraphNodeDatum } from './graphTransforms'
import LinkGraphLegend from './LinkGraphLegend'
import LinkGraphNodePanel from './LinkGraphNodePanel'
import type { LinkGraphCanvasLabels } from './LinkGraphCanvas'

// Only this chunk pulls in react-force-graph-2d; the preview page stays light until graph mode opens.
const LinkGraphCanvas = React.lazy(() => import('./LinkGraphCanvas'))

export interface LinkGraphViewProps {
  file: FileItem
  relatedItems: RelatedFileEntry[]
  relatedLoading: boolean
  linkingRelatedId: number | null
  onLinkFromRelated: (targetId: number) => Promise<void>
  onOpenLinkModal: () => void
  onNavigate: (fileId: number) => void
  onFileLinksChanged: () => Promise<void> | void
  onLoadRelated: () => Promise<void> | void
}

export default function LinkGraphView({
  file,
  relatedItems,
  relatedLoading,
  linkingRelatedId,
  onLinkFromRelated,
  onOpenLinkModal,
  onNavigate,
  onFileLinksChanged,
  onLoadRelated,
}: LinkGraphViewProps) {
  const { t } = useAppTranslation('files')
  const toast = useToast()
  const [wrapperRef, size] = useElementSize<HTMLDivElement>()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [removingLinkId, setRemovingLinkId] = useState<number | null>(null)
  // Content-based suggestions are opt-in: hidden until the user flips the switch in the legend.
  const [showSuggestions, setShowSuggestions] = useState(false)
  const relatedRequestedFor = useRef<number | null>(null)

  const suggestionsAvailable = file.embedding_status === 'completed'

  const { data, explicitEdgeCount, loading, error, truncated, hitCap, expanding, expand, reload } = useLinkGraph({
    centerId: file.id,
    relatedItems: showSuggestions ? relatedItems : [],
    enabled: true,
  })

  // Fetch suggestions lazily, once per file, the first time they are switched on.
  useEffect(() => {
    if (!showSuggestions || !suggestionsAvailable) return
    if (relatedRequestedFor.current === file.id) return
    relatedRequestedFor.current = file.id
    if (relatedItems.length > 0 || relatedLoading) return
    void onLoadRelated()
  }, [showSuggestions, suggestionsAvailable, file.id, relatedItems.length, relatedLoading, onLoadRelated])

  useEffect(() => {
    setSelectedId(null)
  }, [file.id])

  const selectedNode = useMemo(
    () => (selectedId === null ? null : (data.nodes.find((n) => n.id === selectedId) ?? null)),
    [data.nodes, selectedId]
  )

  const explicitLink = useMemo(
    () => (selectedNode ? explicitLinkBetween(data, file.id, selectedNode.id) : undefined),
    [data, file.id, selectedNode]
  )
  const relatedLink = useMemo(
    () => (selectedNode ? relatedLinkBetween(data, file.id, selectedNode.id) : undefined),
    [data, file.id, selectedNode]
  )

  const labels = useMemo<LinkGraphCanvasLabels>(
    () => ({
      relation: (type: FileLinkRelationType) => t(`filePreview.linkTypes.${type}`),
      related: (scorePercent: number) => t('filePreview.linkGraph.relatedScore', { score: `${scorePercent}%` }),
    }),
    [t]
  )

  const handleNodeClick = useCallback((node: GraphNodeDatum) => {
    setSelectedId(node.id)
  }, [])

  const handleNodeDoubleClick = useCallback(
    (node: GraphNodeDatum) => {
      if (node.id === file.id) return
      onNavigate(node.id)
    },
    [file.id, onNavigate]
  )

  const handleBackgroundClick = useCallback(() => setSelectedId(null), [])

  const handleLink = useCallback(async () => {
    if (!selectedNode) return
    await onLinkFromRelated(selectedNode.id)
    await reload()
  }, [onLinkFromRelated, reload, selectedNode])

  const handleRemoveLink = useCallback(async () => {
    if (!explicitLink?.linkId) return
    setRemovingLinkId(explicitLink.linkId)
    try {
      await apiService.deleteFileLink(file.id, explicitLink.linkId)
      toast({
        title: t('filePreview.linkDocuments.unlinkSuccess'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      })
      await onFileLinksChanged()
      await reload()
    } catch {
      toast({
        title: t('filePreview.linkDocuments.unlinkError'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setRemovingLinkId(null)
    }
  }, [explicitLink, file.id, onFileLinksChanged, reload, t, toast])

  const hasSuggestions = showSuggestions && relatedItems.length > 0
  const isEmpty =
    !loading && !error && explicitEdgeCount === 0 && !hasSuggestions && !(showSuggestions && relatedLoading)
  const suggestionsOnly = !loading && !error && explicitEdgeCount === 0 && hasSuggestions

  const legend = (
    <LinkGraphLegend
      truncated={truncated}
      hitCap={hitCap}
      showSuggestions={showSuggestions}
      suggestionsAvailable={suggestionsAvailable}
      onToggleSuggestions={setShowSuggestions}
    />
  )

  return (
    <Box ref={wrapperRef} position="relative" flex={1} minH={0} w="full" h="full" overflow="hidden">
      {loading && (
        <Center h="full">
          <VStack spacing={3}>
            <Spinner color="brand.500" />
            <Text color="gray.600" fontSize="sm">
              {t('filePreview.linkGraph.loading')}
            </Text>
          </VStack>
        </Center>
      )}

      {!loading && error && (
        <Center h="full" px={6}>
          <VStack spacing={3}>
            <Alert status="error" borderRadius="md" maxW="md">
              <AlertIcon />
              {t('filePreview.linkGraph.error')}
            </Alert>
            <Button size="sm" variant="outline" onClick={() => void reload()}>
              {t('filePreview.linkGraph.retry')}
            </Button>
          </VStack>
        </Center>
      )}

      {isEmpty && legend}

      {isEmpty && (
        <Center h="full" px={6}>
          <VStack spacing={3} maxW="sm">
            <Icon as={FiShare2} boxSize={10} color="gray.300" />
            <Text color="gray.600" textAlign="center">
              {t('filePreview.linkGraph.empty')}
            </Text>
            <Button size="sm" colorScheme="brand" onClick={onOpenLinkModal}>
              {t('filePreview.linkGraph.addLink')}
            </Button>
          </VStack>
        </Center>
      )}

      {!loading && !error && !isEmpty && (
        <>
          {legend}

          {suggestionsOnly && (
            <Box
              position="absolute"
              top={3}
              left="50%"
              transform="translateX(-50%)"
              zIndex={5}
              bg="blue.50"
              borderWidth="1px"
              borderColor="blue.200"
              color="blue.800"
              borderRadius="md"
              px={3}
              py={1.5}
              fontSize="xs"
              maxW="60%"
              textAlign="center"
              pointerEvents="none"
            >
              {t('filePreview.linkGraph.emptyHint')}
            </Box>
          )}

          {size.width > 0 && size.height > 0 && (
            <Suspense
              fallback={
                <Center h="full">
                  <Spinner color="brand.500" />
                </Center>
              }
            >
              <LinkGraphCanvas
                data={data}
                width={size.width}
                height={size.height}
                centerId={file.id}
                selectedId={selectedId}
                labels={labels}
                onNodeClick={handleNodeClick}
                onNodeDoubleClick={handleNodeDoubleClick}
                onBackgroundClick={handleBackgroundClick}
              />
            </Suspense>
          )}

          {selectedNode && (
            <LinkGraphNodePanel
              node={selectedNode}
              centerId={file.id}
              explicitLink={explicitLink}
              relatedLink={relatedLink}
              isExpanding={expanding === selectedNode.id}
              isLinking={linkingRelatedId === selectedNode.id}
              isRemoving={removingLinkId !== null && removingLinkId === explicitLink?.linkId}
              onOpen={() => onNavigate(selectedNode.id)}
              onExpand={() => void expand(selectedNode.id)}
              onLink={() => void handleLink()}
              onRemoveLink={() => void handleRemoveLink()}
              onClose={() => setSelectedId(null)}
            />
          )}
        </>
      )}
    </Box>
  )
}
